import React, { useCallback, useContext, useState } from 'react';
import { message, Tooltip, Tabs, Switch } from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Table, Spinner } from 'react-bootstrap';
import { GENERIC_ERROR_MESSAGE, SORTING_STATUS, MEETING_TAKEAWAYS_TABS, PROS_CONS_TOGGLE, USER_TYPES, NO_DATA_MESSAGE, FEATURES } from 'utils/constants';
import { CaretDown, CaretUp } from 'react-bootstrap-icons';
import ViewMeetingTakeaways from './ViewMeetingTakeaways';
import ViewProsAndCons from './ViewProsAndCons';
import { useEffectOnce } from 'hooks/useEffectOnce';
import DemoContext from 'context/DemoContext';
import AuthContext from 'context/AuthContext';
import FeatureGate from 'components/FeatureGate/FeatureGate';
import './MeetingTakeaways.css';

const { TabPane } = Tabs;

// Local constants for this component
const INITIAL_SORTING_STATE = {
  question_text: SORTING_STATUS.NONE,
  count: SORTING_STATUS.NONE,
  product_category: SORTING_STATUS.NONE,
  host_pros: SORTING_STATUS.NONE,
  host_cons: SORTING_STATUS.NONE,
  guest_pros: SORTING_STATUS.NONE,
  guest_cons: SORTING_STATUS.NONE
};

const TOOLTIP_MESSAGES = {
  ASCENDING: 'Click here to sort descending',
  DESCENDING: 'Click here to clear sorting',
  DEFAULT: 'Click here to sort ascending'
};

const COLUMN_NAMES = {
  PRO: 'Pro',
  CON: 'Con'
};

const CSS_CLASSES = {
  ACTIVE: 'active',
  EMPTY: ''
};

const ERROR_DURATION = 5;
const TABLE_HEIGHT = '600px';
const FAQS_TABLE_COLSPAN = '2';
const PROS_CONS_TABLE_COLSPAN = '3';

/**
 * Meeting Takeaways Component - Displays FAQs, Host Pros/Cons, and Guest Pros/Cons in tabbed interface
 *
 * @component
 * @return {ReactNode} A React element that renders the meeting takeaways interface with three tabs
 */
const MeetingTakeaways = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState(MEETING_TAKEAWAYS_TABS.FAQS);
  const [prosConsToggle, setProsConsToggle] = useState(PROS_CONS_TOGGLE.PROS);
  const { userProfile } = useContext(AuthContext);
  const store_identifier = userProfile?.store?.store_id;

  const [sorting, setSorting] = useState(INITIAL_SORTING_STATE);

  // All three tabs are the same insights feature. The page and its tab bar stay
  // visible so the capability is discoverable; only each tab's body is replaced
  // with the upgrade prompt.
  const takeawaysGate = {
    feature: FEATURES.INSIGHTS_PROS_CONS,
    variant: 'block',
    message: 'Meeting takeaways are not included in your current plan.',
  };

  const {
    meetingTakeaways,
    meetingTakeawaysNext,
    getMeetingTakeaways,
    setMeetingTakeaways,
    hostProsAndCons,
    hostProsAndConsNext,
    getHostProsAndCons,
    setHostProsAndCons,
    guestProsAndCons,
    guestProsAndConsNext,
    getGuestProsAndCons,
    setGuestProsAndCons
  } = useContext(DemoContext);

  /**
   * Get current data based on active tab and toggle state
   *
   * @return {Array} Array of data items for the current active tab
   */
  const getCurrentData = () => {
    if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
      return meetingTakeaways;
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.HOST) {
      return hostProsAndCons || [];
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.GUEST) {
      return guestProsAndCons || [];
    } else {
      return [];
    }
  };

  /**
   * Get current next URL for pagination based on active tab
   *
   * @return {string|null} The next URL for pagination or null if no more data
   */
  const getCurrentNext = useCallback(() => {
    if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
      return meetingTakeawaysNext;
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.HOST) {
      return hostProsAndConsNext;
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.GUEST) {
      return guestProsAndConsNext;
    } else {
      return null;
    }
  }, [activeTab, meetingTakeawaysNext, hostProsAndConsNext, guestProsAndConsNext]);

  /**
   * Get current fetch function based on active tab
   *
   * @return {Function|null} The appropriate fetch function for the active tab
   */
  const getCurrentFetchFunction = useCallback(() => {
    if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
      return getMeetingTakeaways;
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.HOST) {
      return getHostProsAndCons;
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.GUEST) {
      return getGuestProsAndCons;
    } else {
      return null;
    }
  }, [activeTab, getMeetingTakeaways, getHostProsAndCons, getGuestProsAndCons]);

  /**
   * This function is used to get the list of current tab data
   *
   * @return {ReactNode} A component returns a list of line items for each tab's data
   */
  const getlist = () => {
    const currentData = getCurrentData();

    // Show loading at the top when loading and no data
    if (loading && currentData?.length === 0) {
      return (
        <tr>
          <td colSpan={getTableColspan()} className="text-center py-4">
            <div className="d-flex justify-content-center">
              <Spinner animation="grow" />
              <Spinner animation="grow" />
              <Spinner animation="grow" />
            </div>
          </td>
        </tr>
      );
    }

    if (!loading && currentData?.length < 1) {
      return (
        <tr>
          <td colSpan={getTableColspan()}>No data available!</td>
        </tr>
      );
    } else {
      return currentData?.map((dataList, index) => {
        if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
          return <ViewMeetingTakeaways key={index} dataList={dataList} />;
        } else {
          return <ViewProsAndCons key={index} dataList={dataList} type={prosConsToggle} userType={activeTab} />;
        }
      });
    }
  };

  /**
   * Fetch more data for infinite scroll pagination
   *
   * @return {void}
   */
  const fetchMoreData = useCallback(async () => {
    const currentNext = getCurrentNext();
    const fetchFunction = getCurrentFetchFunction();

    if (!currentNext || loading || !fetchFunction) {
      setHasMore(false);
      return;
    }
    try {
      setLoading(true);
      const nextPathname = new URL(currentNext).search;

      // For pagination, we want to append data (not replace)
      let actualFetchFunction;
      if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
        actualFetchFunction = params => getMeetingTakeaways(params, false);
      } else if (activeTab === MEETING_TAKEAWAYS_TABS.HOST) {
        actualFetchFunction = params => getHostProsAndCons(params, false);
      } else if (activeTab === MEETING_TAKEAWAYS_TABS.GUEST) {
        actualFetchFunction = params => getGuestProsAndCons(params, false);
      } else {
        // Default fallback case
        actualFetchFunction = params => getMeetingTakeaways(params, false);
      }

      await actualFetchFunction(nextPathname);
      if (currentNext) {
        setHasMore(true);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setHasMore(false);
      messageApi.open({
        type: 'error',
        content: error?.message || GENERIC_ERROR_MESSAGE,
        duration: ERROR_DURATION
      });
    }
  }, [loading, getCurrentNext, getCurrentFetchFunction, activeTab, getMeetingTakeaways, getHostProsAndCons, getGuestProsAndCons, messageApi]);

  /**
   * Get the next sorting direction based on current state
   *
   * @param {string} column - The column name
   * @return {string} The next sorting direction
   */
  const getNextSortingDirection = column => {
    if (sorting[column] === SORTING_STATUS.ASCENDING) {
      return SORTING_STATUS.DESCENDING;
    } else if (sorting[column] === SORTING_STATUS.DESCENDING) {
      return SORTING_STATUS.NONE;
    } else {
      return SORTING_STATUS.ASCENDING;
    }
  };

  /**
   * Clear all data arrays to prevent showing stale data
   *
   * @return {void}
   */
  const clearAllDataArrays = () => {
    setMeetingTakeaways([]);
    setHostProsAndCons([]);
    setGuestProsAndCons([]);
  };

  /**
   * Build query parameters for sorting request
   *
   * @param {string} column - The column name
   * @param {string} sortingDirection - The sorting direction
   * @return {string} The complete query string
   */
  const buildSortingQueryParams = (column, sortingDirection) => {
    let queryParams = `?order=true&store_identifier=${store_identifier}`;

    // Add specific parameters based on active tab
    if (activeTab === MEETING_TAKEAWAYS_TABS.HOST) {
      queryParams += `&user_type=${USER_TYPES.HOST}`;
      queryParams += `&type=${prosConsToggle}`;
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.GUEST) {
      queryParams += `&user_type=${USER_TYPES.GUEST}`;
      queryParams += `&type=${prosConsToggle}`;
    } else {
      queryParams += `&type=${prosConsToggle}`;
    }
    // FAQs tab doesn't need additional parameters

    // Map column names to backend parameter names
    let backendParamName;
    if (column === 'count') {
      backendParamName = 'count';
    } else if (column === 'question_text') {
      backendParamName = 'text';
    } else if (column === 'product_category') {
      backendParamName = 'product_category';
    } else if (column.includes('pros') || column.includes('cons')) {
      // For host_pros, host_cons, guest_pros, guest_cons - use 'text'
      backendParamName = 'text';
    } else {
      // Default fallback
      backendParamName = column;
    }

    queryParams += `&${backendParamName}=${sortingDirection}`;
    return queryParams;
  };

  /**
   * Get the appropriate fetch function for sorting
   *
   * @return {Function} The fetch function for the current tab
   */
  const getSortFetchFunction = () => {
    if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
      return params => getMeetingTakeaways(params, true);
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.HOST) {
      return params => getHostProsAndCons(params, true);
    } else if (activeTab === MEETING_TAKEAWAYS_TABS.GUEST) {
      return params => getGuestProsAndCons(params, true);
    } else {
      // Default fallback case
      return params => getMeetingTakeaways(params, true);
    }
  };

  /**
   * Get the next sorting state for a specific column
   *
   * @param {string} key - The column key
   * @param {string} column - The column being sorted
   * @param {string} currentValue - The current sorting value
   * @return {string} The next sorting state
   */
  const getNextColumnSortingState = (key, column, currentValue) => {
    if (key === column && currentValue === SORTING_STATUS.ASCENDING) {
      return SORTING_STATUS.DESCENDING;
    } else if (key === column && sorting[column] === SORTING_STATUS.DESCENDING) {
      return SORTING_STATUS.NONE;
    } else if (key === column && sorting[column] === SORTING_STATUS.NONE) {
      return SORTING_STATUS.ASCENDING;
    } else {
      return SORTING_STATUS.NONE;
    }
  };

  /**
   * Update sorting state after successful sort
   *
   * @param {string} column - The column that was sorted
   * @return {void}
   */
  const updateSortingState = column => {
    const newSorting = { ...sorting };
    Object.entries(newSorting).forEach(([key, value]) => {
      newSorting[key] = getNextColumnSortingState(key, column, value);
    });
    setSorting(newSorting);
  };

  /**
   * Handle column sorting for table data
   *
   * @param {string} column - The column name to sort by
   * @return {void}
   */
  const handleSorting = column => {
    if (loading) {
      return;
    }

    const sortingDirection = getNextSortingDirection(column);

    setHasMore(true);
    setLoading(true);
    clearAllDataArrays();

    const queryParams = buildSortingQueryParams(column, sortingDirection);
    const sortFetchFunction = getSortFetchFunction();

    const res = sortFetchFunction(queryParams);
    res
      .then(() => {
        updateSortingState(column);
        setLoading(false);
        setHasMore(true);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  /**
   * Get tooltip message for column sorting
   *
   * @param {string} columnName - The name of the column
   * @return {string} Tooltip message to display
   */
  const getTooltipTitle = columnName => {
    if (sorting[columnName] === SORTING_STATUS.ASCENDING) {
      return TOOLTIP_MESSAGES.ASCENDING;
    } else if (sorting[columnName] === SORTING_STATUS.DESCENDING) {
      return TOOLTIP_MESSAGES.DESCENDING;
    } else {
      return TOOLTIP_MESSAGES.DEFAULT;
    }
  };

  /**
   * Get the toggle state based on checked value
   *
   * @param {boolean} checked - Whether the switch is checked
   * @return {string} The toggle state ('cons' or 'pros')
   */
  const getToggleState = checked => {
    if (checked) {
      return PROS_CONS_TOGGLE.CONS;
    } else {
      return PROS_CONS_TOGGLE.PROS;
    }
  };

  /**
   * Get the column name based on pros/cons toggle
   *
   * @param {string} toggleState - The current toggle state
   * @return {string} The column name ('Pro' or 'Con')
   */
  const getColumnName = toggleState => {
    if (toggleState === PROS_CONS_TOGGLE.PROS) {
      return COLUMN_NAMES.PRO;
    } else {
      return COLUMN_NAMES.CON;
    }
  };

  /**
   * Get the sorting key based on active tab and toggle state
   *
   * @param {string} tabKey - The active tab key
   * @param {string} toggleState - The current toggle state
   * @return {string} The sorting key
   */
  const getSortingKey = (tabKey, toggleState) => {
    if (tabKey === MEETING_TAKEAWAYS_TABS.HOST) {
      if (toggleState === PROS_CONS_TOGGLE.PROS) {
        return 'host_pros';
      } else {
        return 'host_cons';
      }
    } else {
      if (toggleState === PROS_CONS_TOGGLE.PROS) {
        return 'guest_pros';
      } else {
        return 'guest_cons';
      }
    }
  };

  /**
   * Get the CSS class for pros/cons toggle button
   *
   * @param {string} currentToggle - The current toggle state
   * @param {string} targetToggle - The target toggle to check against
   * @return {string} The CSS class ('active' or '')
   */
  const getToggleButtonClass = (currentToggle, targetToggle) => {
    if (currentToggle === targetToggle) {
      return CSS_CLASSES.ACTIVE;
    } else {
      return CSS_CLASSES.EMPTY;
    }
  };

  /**
   * Handle pros/cons toggle switch change
   *
   * @param {boolean} checked - Whether the switch is checked (true = cons, false = pros)
   * @return {void}
   */
  const handleToggleChange = checked => {
    const newToggleState = getToggleState(checked);
    setProsConsToggle(newToggleState);

    // Only reload data if we're currently on a pros/cons tab
    if (activeTab !== MEETING_TAKEAWAYS_TABS.FAQS) {
      setLoading(true);

      // Immediately clear all data arrays to prevent showing old data
      clearAllDataArrays();

      // Load data with new toggle state (replace mode will clear old data)
      loadTabData(activeTab, newToggleState, true);
    }
  };

  /**
   * Build query parameters for tab data loading
   *
   * @param {string} tabKey - The tab key
   * @param {string} currentToggleState - The current toggle state
   * @param {string} storeId - The store identifier
   * @return {string} The complete query string
   */
  const buildTabQueryParams = (tabKey, currentToggleState, storeId) => {
    let queryParams = `?order=true&store_identifier=${storeId}`;

    if (tabKey === MEETING_TAKEAWAYS_TABS.HOST) {
      queryParams += `&user_type=${USER_TYPES.HOST}&type=${currentToggleState}`;
    } else if (tabKey === MEETING_TAKEAWAYS_TABS.GUEST) {
      queryParams += `&user_type=${USER_TYPES.GUEST}&type=${currentToggleState}`;
    } else {
      queryParams += `&type=${currentToggleState}`;
    }

    return queryParams;
  };

  /**
   * Get the appropriate fetch function for tab data loading
   *
   * @param {string} tabKey - The tab key
   * @param {boolean} isInitialLoad - Whether this is an initial load
   * @param {Object} fetchFunctions - Object containing all fetch functions
   * @return {Function} The fetch function for the tab
   */
  const getTabFetchFunction = (tabKey, isInitialLoad, fetchFunctions) => {
    const { getMeetingTakeaways: getMeetingTakeawaysFn, getHostProsAndCons: getHostProsAndConsFn, getGuestProsAndCons: getGuestProsAndConsFn } = fetchFunctions;

    if (tabKey === MEETING_TAKEAWAYS_TABS.FAQS) {
      return params => getMeetingTakeawaysFn(params, isInitialLoad);
    } else if (tabKey === MEETING_TAKEAWAYS_TABS.HOST) {
      return params => getHostProsAndConsFn(params, isInitialLoad);
    } else if (tabKey === MEETING_TAKEAWAYS_TABS.GUEST) {
      return params => getGuestProsAndConsFn(params, isInitialLoad);
    } else {
      // Default fallback case
      return params => getMeetingTakeawaysFn(params, isInitialLoad);
    }
  };

  /**
   * Load data for specific tab with appropriate parameters
   *
   * @param {string} tabKey - The key of the tab to load data for ('faqs', 'host', 'guest')
   * @param {string} toggleState - The current toggle state ('pros' or 'cons')
   * @param {boolean} isInitialLoad - Whether this is an initial load (replace data) or pagination (append data)
   * @return {void}
   */
  const loadTabData = useCallback((tabKey, toggleState, isInitialLoad = true) => {
    const currentToggleState = toggleState || prosConsToggle;
    const queryParams = buildTabQueryParams(tabKey, currentToggleState, store_identifier);
    const fetchFunction = getTabFetchFunction(tabKey, isInitialLoad, { getMeetingTakeaways, getHostProsAndCons, getGuestProsAndCons });

    if (fetchFunction) {
      const res = fetchFunction(queryParams);
      res
        .then(() => {
          setLoading(false);
        })
        .catch(err => {
          messageApi.open({
            type: 'error',
            content: err?.message || GENERIC_ERROR_MESSAGE,
            duration: ERROR_DURATION
          });
          setLoading(false);
        });
    }
  }, [store_identifier, prosConsToggle, getMeetingTakeaways, getHostProsAndCons, getGuestProsAndCons, messageApi]);

  /**
   * Handle tab change event
   *
   * @param {string} key - The key of the selected tab
   * @return {void}
   */
  const handleTabChange = key => {
    setActiveTab(key);
    setLoading(true);
    setHasMore(true);

    // Immediately clear all data arrays to prevent showing old data
    clearAllDataArrays();

    // Reset sorting when changing tabs
    setSorting(INITIAL_SORTING_STATE);

    // Load data for the new tab (replace mode will clear old data)
    loadTabData(key, prosConsToggle, true);
  };

  /**
   * Get the appropriate colspan for table cells based on active tab
   *
   * @return {string} The colspan value
   */
  const getTableColspan = () => {
    if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
      return FAQS_TABLE_COLSPAN;
    } else {
      return PROS_CONS_TABLE_COLSPAN;
    }
  };

  /**
   * Get table headers based on active tab
   *
   * @return {ReactNode} JSX elements for table headers
   */
  const getTableHeaders = () => {
    if (activeTab === MEETING_TAKEAWAYS_TABS.FAQS) {
      return (
        <>
          <th className="question-head">
            <div className="arrows">
              Question{' '}
              <Tooltip title={getTooltipTitle('question_text')}>
                <div
                  className="arrow-grid"
                  onClick={() => {
                    handleSorting('question_text');
                  }}
                >
                  <CaretUp />
                  <CaretDown />
                </div>
              </Tooltip>
            </div>
          </th>
          <th className="count-head">
            <div className="arrows">
              Occurrences{' '}
              <Tooltip title={getTooltipTitle('count')}>
                <div
                  className="arrow-grid"
                  onClick={() => {
                    handleSorting('count');
                  }}
                >
                  <CaretUp />
                  <CaretDown />
                </div>
              </Tooltip>
            </div>
          </th>
        </>
      );
    } else {
      const columnName = getColumnName(prosConsToggle);
      const sortingKey = getSortingKey(activeTab, prosConsToggle);

      return (
        <>
          <th className="question-head">
            <div className="arrows">
              {columnName}{' '}
              <Tooltip title={getTooltipTitle(sortingKey)}>
                <div
                  className="arrow-grid"
                  onClick={() => {
                    handleSorting(sortingKey);
                  }}
                >
                  <CaretUp />
                  <CaretDown />
                </div>
              </Tooltip>
            </div>
          </th>
          <th className="count-head">
            <div className="arrows">
              Occurrences{' '}
              <Tooltip title={getTooltipTitle('count')}>
                <div
                  className="arrow-grid"
                  onClick={() => {
                    handleSorting('count');
                  }}
                >
                  <CaretUp />
                  <CaretDown />
                </div>
              </Tooltip>
            </div>
          </th>
          <th className="product-category-head">
            <div className="arrows">
              Product Category{' '}
              <Tooltip title={getTooltipTitle('product_category')}>
                <div
                  className="arrow-grid"
                  onClick={() => {
                    handleSorting('product_category');
                  }}
                >
                  <CaretUp />
                  <CaretDown />
                </div>
              </Tooltip>
            </div>
          </th>
        </>
      );
    }
  };

  useEffectOnce(() => {
    loadTabData(MEETING_TAKEAWAYS_TABS.FAQS, prosConsToggle);

    return () => {
      clearAllDataArrays();
    };
  }, []);

  return (
    <>
      {contextHolder}
      <div className="meeting-takeaways-container">
        <Tabs activeKey={activeTab} onChange={handleTabChange} className="takeaways-tabs">
          <TabPane tab="FAQs" key={MEETING_TAKEAWAYS_TABS.FAQS}>
            <FeatureGate {...takeawaysGate}>
            <div
              className="table-container"
              id="takeawayScrollableDiv"
              style={{ height: TABLE_HEIGHT, overflow: 'auto' }}
            >
              <InfiniteScroll
                dataLength={getCurrentData()?.length || 0}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  loading && getCurrentData()?.length > 0 && (
                    <div className="d-flex justify-content-center">
                      <Spinner animation="grow" />
                      <Spinner animation="grow" />
                      <Spinner animation="grow" />
                    </div>
                  )
                }
                scrollableTarget="takeawayScrollableDiv"
                endMessage={
                  <div className="text-center py-3 text-muted">
                    {!hasMore && NO_DATA_MESSAGE}
                  </div>
                }
              >
                <div className="store-payout-history-wrapper table-responsive">
                  <Table className="table-meeting-takeaways">
                    <thead>
                      <tr>
                        {getTableHeaders()}
                      </tr>
                    </thead>
                    <tbody id="takeawayScrollableDiv">{getlist()}</tbody>
                  </Table>
                </div>
              </InfiniteScroll>
            </div>
            </FeatureGate>
          </TabPane>

          <TabPane tab="Real Customer Pros & Cons" key={MEETING_TAKEAWAYS_TABS.HOST}>
            <FeatureGate {...takeawaysGate}>
            <div className="pros-cons-controls">
              <div className="toggle-container">
                <span className={getToggleButtonClass(prosConsToggle, PROS_CONS_TOGGLE.PROS)}>Pros</span>
                <Switch
                  checked={prosConsToggle === PROS_CONS_TOGGLE.CONS}
                  onChange={handleToggleChange}
                  className="pros-cons-switch"
                />
                <span className={getToggleButtonClass(prosConsToggle, PROS_CONS_TOGGLE.CONS)}>Cons</span>
              </div>
            </div>
            <div
              className="table-container"
              id="hostScrollableDiv"
              style={{ height: TABLE_HEIGHT, overflow: 'auto' }}
            >
              <InfiniteScroll
                dataLength={getCurrentData()?.length || 0}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  loading && getCurrentData()?.length > 0 && (
                    <div className="d-flex justify-content-center">
                      <Spinner animation="grow" />
                      <Spinner animation="grow" />
                      <Spinner animation="grow" />
                    </div>
                  )
                }
                scrollableTarget="hostScrollableDiv"
                endMessage={
                  <div className="text-center py-3 text-muted">
                    {!hasMore && NO_DATA_MESSAGE}
                  </div>
                }
              >
                <div className="store-payout-history-wrapper table-responsive">
                  <Table className="table-meeting-takeaways">
                    <thead>
                      <tr>
                        {getTableHeaders()}
                      </tr>
                    </thead>
                    <tbody id="hostScrollableDiv">{getlist()}</tbody>
                  </Table>
                </div>
              </InfiniteScroll>
            </div>
            </FeatureGate>
          </TabPane>

          <TabPane tab="Guest Pros & Cons" key={MEETING_TAKEAWAYS_TABS.GUEST}>
            <FeatureGate {...takeawaysGate}>
            <div className="pros-cons-controls">
              <div className="toggle-container">
                <span className={getToggleButtonClass(prosConsToggle, PROS_CONS_TOGGLE.PROS)}>Pros</span>
                <Switch
                  checked={prosConsToggle === PROS_CONS_TOGGLE.CONS}
                  onChange={handleToggleChange}
                  className="pros-cons-switch"
                />
                <span className={getToggleButtonClass(prosConsToggle, PROS_CONS_TOGGLE.CONS)}>Cons</span>
              </div>
            </div>
            <div
              className="table-container"
              id="guestScrollableDiv"
              style={{ height: TABLE_HEIGHT, overflow: 'auto' }}
            >
              <InfiniteScroll
                dataLength={getCurrentData()?.length || 0}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  loading && getCurrentData()?.length > 0 && (
                    <div className="d-flex justify-content-center">
                      <Spinner animation="grow" />
                      <Spinner animation="grow" />
                      <Spinner animation="grow" />
                    </div>
                  )
                }
                scrollableTarget="guestScrollableDiv"
                endMessage={
                  <div className="text-center py-3 text-muted">
                    {!hasMore && NO_DATA_MESSAGE}
                  </div>
                }
              >
                <div className="store-payout-history-wrapper table-responsive">
                  <Table className="table-meeting-takeaways">
                    <thead>
                      <tr>
                        {getTableHeaders()}
                      </tr>
                    </thead>
                    <tbody id="guestScrollableDiv">{getlist()}</tbody>
                  </Table>
                </div>
              </InfiniteScroll>
            </div>
            </FeatureGate>
          </TabPane>
        </Tabs>
      </div>
    </>
  );
};

export default MeetingTakeaways;
